import {z} from 'zod';

import {customerMessageResponseBodySchema, TaskResponseBodySchema} from '@dms/api/core';
import {UuidResponseBodySchema} from '@dms/api/shared';

export const ServiceCaseActivityKindSchema = z.enum([
  'SERVICE_CASE_CREATED',
  'SERVICE_CASE_PLANNED',
  'SERVICE_CASE_CHECK_IN_COMPLETED',
  'SERVICE_CASE_HANDOVER_COMPLETED',
  'EMAIL_TO_CUSTOMER',
  'SMS_TO_CUSTOMER',
  'WHATSAPP_TO_CUSTOMER',
  'CALL_FROM_CUSTOMER',
  'CALL_TO_CUSTOMER',
  'TELEPHONY_INBOUND_CALL',
  'TELEPHONY_OUTBOUND_CALL',
  'MESSAGE_FROM_CUSTOMER',
  'MESSAGE_TO_CUSTOMER',
  'CUSTOMER_VISIT',
  'TASK_CREATED',
  'TASK_FINISHED',
  'BUSINESS_CASE_LINKED',
]);
export type ServiceCaseActivityKind = z.infer<typeof ServiceCaseActivityKindSchema>;

const ServiceCaseActivityRelatedTaskResponseBodySchema = TaskResponseBodySchema.pick({
  id: true,
  title: true,
  priority: true,
  type: true,
  state: true,
  assignedTo: true,
  dueDate: true,
  dueFrom: true,
  dueTo: true,
  finishedAt: true,
  description: true,
});

const ServiceCaseActivityRelatedMessageResponseBodySchema = z.object({
  id: z.string(),
  body: z.string(),
  subject: z.string().nullable().optional(),
  sentTo: z.string().nullable().optional(),
  attachments: z
    .array(
      z.object({
        filename: z.string(),
        uri: z.string(),
        contentType: z.string().nullable().optional(),
        size: z.number().nullable().optional(),
      })
    )
    .nullable()
    .optional(),
  sender: z.object({name: z.string(), email: z.string()}),
});

export const ServiceCaseActivityResponseBodySchema = z.object({
  id: z.string(),
  interestId: z.string().nullable(),
  interestCode: z.string().nullable(),
  interestType: z.string().nullable(),
  subject: z.string().nullable(),
  activityType: ServiceCaseActivityKindSchema,
  userId: z.string().nullable(),
  userName: z.string(),
  timestamp: z.string(),
  relatedTask: ServiceCaseActivityRelatedTaskResponseBodySchema.nullable(),
  relatedMessage: ServiceCaseActivityRelatedMessageResponseBodySchema.nullable(),
  description: z.string().nullable(),
  reasonCloseCodeId: z.string().nullable(),
  reasonCloseNote: z.string().nullable(),
  relatedCall: z.object({id: z.string()}).nullable(),
  isEditable: z.boolean(),
  relatedChatConversation: z.object({id: z.string()}).nullable(),
  relatedTestDrive: z
    .object({
      id: z.string(),
      vehicleId: z.string(),
      userId: z.string(),
      returnedAt: z.string().nullable(),
      scheduledAt: z.string(),
    })
    .nullable(),
  onlineOfferEngagement: z
    .object({
      activeTimeMs: z.number(),
      maxScrollDepth: z.union([
        z.literal(0),
        z.literal(25),
        z.literal(50),
        z.literal(75),
        z.literal(100),
      ]),
    })
    .nullable(),
  businessCaseId: z.string().nullable(),
  businessCaseCode: z.string().nullable(),
  serviceOrderId: z.string().nullable(),
  serviceOrderNumber: z.string().nullable(),
});
export type ServiceCaseActivityResponseBody = z.infer<typeof ServiceCaseActivityResponseBodySchema>;

export const GetServiceCaseActivityApiArgSchema = z.object({serviceCaseId: z.string()});
export type GetServiceCaseActivityApiArg = z.infer<typeof GetServiceCaseActivityApiArgSchema>;
export const GetServiceCaseActivityApiResponseSchema =
  ServiceCaseActivityResponseBodySchema.array();
export type GetServiceCaseActivityApiResponse = z.infer<
  typeof GetServiceCaseActivityApiResponseSchema
>;

export const ServiceCaseManualActivityKindSchema = z.enum([
  'CALL_FROM_CUSTOMER',
  'CALL_TO_CUSTOMER',
  'MESSAGE_FROM_CUSTOMER',
  'MESSAGE_TO_CUSTOMER',
  'CUSTOMER_VISIT',
]);
export type ServiceCaseManualActivityKind = z.infer<typeof ServiceCaseManualActivityKindSchema>;

export const CreateServiceCaseActivityRequestBodySchema = z.object({
  activityType: ServiceCaseManualActivityKindSchema,
  createdAt: z.string(),
  description: z.string().nullable(),
  subject: z.string().nullable(),
});
export type CreateServiceCaseActivityRequestBody = z.infer<
  typeof CreateServiceCaseActivityRequestBodySchema
>;
export const CreateServiceCaseActivityApiArgSchema = z.object({
  serviceCaseId: z.string(),
  createServiceCaseActivityRequestBody: CreateServiceCaseActivityRequestBodySchema,
});
export type CreateServiceCaseActivityApiArg = z.infer<typeof CreateServiceCaseActivityApiArgSchema>;
export const CreateServiceCaseActivityApiResponseSchema = UuidResponseBodySchema;
export type CreateServiceCaseActivityApiResponse = z.infer<
  typeof CreateServiceCaseActivityApiResponseSchema
>;

export const GetServiceCaseUpcomingTasksApiArgSchema = z.object({serviceCaseId: z.string()});
export type GetServiceCaseUpcomingTasksApiArg = z.infer<
  typeof GetServiceCaseUpcomingTasksApiArgSchema
>;
export const GetServiceCaseUpcomingTasksApiResponseSchema = TaskResponseBodySchema.array();
export type GetServiceCaseUpcomingTasksApiResponse = z.infer<
  typeof GetServiceCaseUpcomingTasksApiResponseSchema
>;

export const GetServiceCaseCustomerMessagesApiArgSchema = z.object({
  serviceCaseId: z.string(),
  limit: z.number().optional(),
  offset: z.number().optional(),
});
export type GetServiceCaseCustomerMessagesApiArg = z.infer<
  typeof GetServiceCaseCustomerMessagesApiArgSchema
>;
export const GetServiceCaseCustomerMessagesApiResponseSchema = z.object({
  messages: customerMessageResponseBodySchema.array(),
});
export type GetServiceCaseCustomerMessagesApiResponse = z.infer<
  typeof GetServiceCaseCustomerMessagesApiResponseSchema
>;
